import { Injectable } from '@angular/core';
import {ConfigurationService} from './configuration/configuration.service';
import {DetailCoverageGroup, DetailCoverageGroupCode} from '../../configuration/oc-configuration';
import {Offer} from '../../models/offer/offer.model';
import {OfferCoverageDestiny} from '../../models/offer/offer-coverage-destiny.model';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {OfferPrimaryCoverageCode} from '../../consts/offer-primary-coverage-code.enum';
import * as _ from 'lodash';
import {SortOrder} from '../../../shared/enums/sort-order.enum';
import {InsuranceCompanyCode} from '../../../shared/models/insurance-company-code.enum';
import {BusinessParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {LanguageService} from '../../../shared/services/language.service';
import {SeparatorPipe} from '../../../shared/pipes/separator.pipe';
import {OfferCoverageDestinyCode} from '../../enums/offer/offer-coverage-destiny-code.enum';

@Injectable({
  providedIn: 'root'
})
export class OfferCoveragesService {

  get detailCoverageGroups(): DetailCoverageGroup[] {
    return this.configurationService.configuration.detailCoverageGroups;
  }

  constructor(private configurationService: ConfigurationService,
              private stateService: ApplicationStateService,
              private separatorPipe: SeparatorPipe,
              private languageService: LanguageService) { }

  createDetailCoverageGroupBoxes(offer: Offer): DetailCoverageGroupBox[] {
    return this.detailCoverageGroups.map(coverageGroup => ({
      groupCode: coverageGroup.code,
      boxes: coverageGroup.coverages.map(coverage => {
        const destiny = this.getCoverageDestiny(offer, coverage.code, OfferCoverageDestinyCode.DETAIL_BOX);
        if (isNotNullOrUndefined(destiny)) {
          destiny.coverageCode = coverage.code;
        }
        return destiny;
      })
        .filter(box => isNotNullOrUndefined(box)),
      mobileCoverages: this.getMobileCoverages(coverageGroup)
    }));
  }

  hasOfferPrimaryCoverage(offer: Offer, coverageCode: string): boolean {
    const coverage = offer.coverages.find(item => item.code === coverageCode);
    if (isNotNullOrUndefined(coverage)) {
      const primaryBox = coverage.destinies.find(destiny => destiny.code === OfferCoverageDestinyCode.PRIMARY_BOX);
      return !!primaryBox && primaryBox.selectedOption.includes('YES');
    }
    return false;
  }

  hasOfferPrimaryCoverages(offer: Offer, coverageCodes: string[]): boolean {
    return coverageCodes.map(code => {
      if (code === OfferPrimaryCoverageCode.AC) {
        return this.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC) || this.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC_MINI);
      }
      return this.hasOfferPrimaryCoverage(offer, code);
    })
        .every(value => value);
  }

  hasOfferExactPrimaryCoverages(offer: Offer, coverageCodes: string[]): boolean {
    const offerCoverages = this.getOfferPrimaryCoverages(offer);
    return _.isEqual(offerCoverages.sort(), coverageCodes.sort());
  }

  isBoxVisibleOnMobile(group: DetailCoverageGroupBox, coverageCode: string): boolean {
    return group.mobileCoverages.includes(coverageCode);
  }

  getCheapestOffer(offers: Offer[]): Offer {
    return offers.sort((a: Offer, b: Offer) => Offer.getOfferLowestPrice(a) - Offer.getOfferLowestPrice(b))[0];
  }

  getCheapestOfferForInsuranceCompany(offers: Offer[], icCode: InsuranceCompanyCode): Offer {
    const icOffers = this.getInsuranceCompanyOffers(offers, icCode);
    return this.getCheapestOffer(icOffers);
  }

  getOffersWithExactPrimaryCoverages(offers: Offer[], coverages: OfferPrimaryCoverageCode[]): Offer[] {
    return offers.filter(offer => {
      const offerCoverages = this.getOfferPrimaryCoverages(offer);
      return _.isEqual(offerCoverages.sort(), coverages.sort());
    })
  }

  getOffersWithPrimaryCoverages(offers: Offer[], coverages: OfferPrimaryCoverageCode[]): Offer[] {
    return offers.filter(offer =>  coverages.every(coverage => this.hasOfferPrimaryCoverage(offer, coverage)));
  }

  getInsuranceCompanyOffers(offers: Offer[], icCode: InsuranceCompanyCode): Offer[] {
    return offers.filter(offer => this.stateService.getBusinessParamFromOffer(offer.id, BusinessParamName.INSURANCE_COMPANY_CODE) === icCode);
  }

  shouldDisplayDetailBox(box: OfferCoverageDestiny): boolean {
    const { options, selectedOption, enabled } = box;
    if (enabled) {
      let offerCoverageDestinyOptions = options.filter(option => option.code === selectedOption);
      if (offerCoverageDestinyOptions.length === 0) {
        return false;
      }

      const codes = options.map(({code}) => code);
      if (!codes.includes(selectedOption)) {
        return false;
      }

      const offerCoverageDestinyOption = offerCoverageDestinyOptions[0];
      const optionValues = offerCoverageDestinyOption.values;
      return this.languageService.getBooleanOption(optionValues);
    }
    return false;
  }

  getBoxLabel(box: OfferCoverageDestiny): string {
    return this.languageService.getLabel(<any[]>box.labels);
  }

  getBoxValue(box: OfferCoverageDestiny): any {
    const selectedOption = box.options.find(option => option.code === box.selectedOption);
    const value = this.languageService.getTranslationForKey(selectedOption.values, 'value');
    if (typeof value === 'number') {
      const currency = this.languageService.translateService.instant('OFFER_SECTION.SHARED.currencyShortcut');
      return `${this.separatorPipe.transform(value)} ${currency}`;
    }
    return value;
  }

  getCoverageDestiny(offer: Offer, coverageCode: string, destinyCode: string): OfferCoverageDestiny {
    const offerCoverage = offer.coverages.find(coverage => coverage.code === coverageCode);
    return offerCoverage ? offerCoverage.destinies.find(destiny => destiny.code === destinyCode) : null;
  }

  private getOfferPrimaryCoverages(offer: Offer): string[] {
    return offer.coverages
        .filter(coverage => {
          if (Object.keys(OfferPrimaryCoverageCode).includes(coverage.code)) {
            const primaryBoxDestiny = coverage.destinies.find(destiny => destiny.code === OfferCoverageDestinyCode.PRIMARY_BOX);
            return primaryBoxDestiny.selectedOption === `${coverage.code}_YES`;
          }
        })
        .map(coverage => coverage.code);
  }

  private getMobileCoverages(group: DetailCoverageGroup): string[] {
    return group.coverages
        .filter(coverage => coverage.mobile)
        .map(coverage => coverage.code);
  }

}

export interface DetailCoverageGroupBox {
  groupCode: DetailCoverageGroupCode;
  boxes: OfferCoverageDestiny[];
  mobileCoverages: string[];
}
